import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Flame, Loader2, Sparkles, ArrowRight, Zap, Hand } from "lucide-react";

const TYPE_COLOR = {
  Objection: "text-amber-600 bg-amber-500/10 border-amber-500/30",
  Personality: "text-red-600 bg-red-500/10 border-red-500/30",
  Timed: "text-cyan-600 bg-cyan-500/10 border-cyan-500/30",
  Confidence: "text-violet-600 bg-violet-500/10 border-violet-500/30",
  Closing: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  Mindset: "text-pink-600 bg-pink-500/10 border-pink-500/30",
};

export default function Challenges() {
  const navigate = useNavigate();
  const [ch, setCh] = useState(null);

  useEffect(() => { api.get("/challenges/daily").then((r) => setCh(r.data)).catch((err) => console.warn("[Challenges] request failed", err)); }, []);
  if (!ch) return <div className="flex items-center justify-center py-32 text-emerald-600"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const start = () => navigate("/app/roleplay");

  return (
    <div className="space-y-6">
      <div>
        <p className="text-amber-600 font-mono text-xs uppercase tracking-widest flex items-center gap-2"><Flame className="w-4 h-4" /> Daily challenge</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Today's Challenge</h1>
        <p className="text-slate-500 mt-2">A fresh drill every day. Complete it to keep your streak alive.</p>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-surface rounded-2xl overflow-hidden">
        <div className="relative h-44">
          {ch.persona && <img src={ch.persona.avatar} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF] via-[#FFFFFF]/40 to-transparent" />
          <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${TYPE_COLOR[ch.type] || TYPE_COLOR.Objection}`}>{ch.type}</span>
          <span className="absolute top-4 right-4 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-600 text-xs font-mono flex items-center gap-1"><Sparkles className="w-3 h-3" /> +{ch.xp} XP</span>
        </div>
        <div className="p-6">
          <h2 className="text-2xl font-bold text-slate-900 font-display">{ch.title}</h2>
          <p className="text-slate-500 mt-2 leading-relaxed">{ch.brief}</p>
          {ch.persona && (
            <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
              <Zap className="w-4 h-4 text-emerald-600" /> Facing: <span className="text-slate-600">{ch.persona.title}</span>
            </div>
          )}
          <Button onClick={start} className="mt-6 bg-amber-500 hover:bg-amber-600 text-black font-semibold h-11 px-6" data-testid="start-challenge-btn">
            Take the challenge <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="card-surface rounded-2xl p-5 border border-red-500/25" data-testid="comeback-drill-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
            <Hand className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-slate-900">Comeback Drill</h2>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase border border-red-500/30 bg-red-500/10 text-red-600">Interrupt training</span>
            </div>
            <p className="text-slate-500 mt-1 text-sm leading-relaxed">The customer will cut you off, talk over you, and fire curveballs. Stay calm and rehearse regaining control — you'll get an Interrupt Recovery score.</p>
            <Button onClick={() => navigate("/app/roleplay?mode=comeback")} className="mt-4 bg-red-500 hover:bg-red-600 text-white font-semibold" data-testid="start-comeback-drill-btn">
              Start the drill <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </motion.div>

      <div className="grid sm:grid-cols-3 gap-4">
        {["Confidence drills", "Closing drills", "Mindset exercises"].map((t, i) => (
          <div key={t} className="card-surface rounded-2xl p-5 opacity-70" data-testid={`drill-${i}`}>
            <div className="text-sm font-semibold text-slate-900">{t}</div>
            <div className="text-xs text-slate-500 mt-1">Unlocks as you level up</div>
          </div>
        ))}
      </div>
    </div>
  );
}
